import { AxiosInstance, AxiosResponse, ResponseType } from 'axios'
import { NodeAxios, PythonAxios } from '.'

export interface CreateApiOptions {
    url: string
    queryParams?: string
    data?: Record<string, unknown> | FormData
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE'
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
    }
    /** Request timeout in ms; uses axios instance default if not set */
    timeout?: number
    server?: 'node' | 'python'
}

export const createApiRequest = async <TData>(options: CreateApiOptions): Promise<TData> => {
    const { url, queryParams, method, data, apiOptions = {}, server, timeout } = options

    // Use URLSearchParams for proper encoding and concatenation of query parameters
    const searchParams = new URLSearchParams(queryParams ?? '')
    const queryString = searchParams.toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url

    // Select Axios instance based on the server
    const axiosInstance: AxiosInstance = server === 'python' ? PythonAxios : NodeAxios

    try {
        // Use await directly with async/await syntax
        const response: AxiosResponse<TData> = await axiosInstance({
            url: fullUrl,
            method,
            onUploadProgress: () => {},
            ...(timeout != null ? { timeout } : {}),
            ...(data ? { data } : {}),
            ...apiOptions,
            headers: {
                // For FormData: let browser set Content-Type with boundary
                // For JSON: explicitly set application/json
                ...(data instanceof FormData ? {} : { 'Content-Type': 'application/json', ...apiOptions.headers }),
                'ngrok-skip-browser-warning': true,
            },
        })

        // Because of the Axios response interceptor in services/api/index.ts,
        // 'response' here is actually already 'response.data' if it's a success.
        // We need to handle both cases to be safe.
        const responseData = (response as any).data !== undefined ? (response as any).data : response

        if (apiOptions.responseType === 'blob') {
            // For blobs (e.g. PDFs, ZIPs), return the raw data (Blob)
            return responseData as unknown as TData
        }

        return responseData
    } catch (error) {
        console.log('Error in createApiRequest', error)
        // Rethrow the error for the caller to handle
        throw error
    }
}
